import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { ProductCard } from "../components/public/ProductCard";
import { Sparkles, Leaf, Heart, Users, ArrowRight, MapPin, Clock } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1746635732995-ab5c6118d5a7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

export default function Home() {
  const { t, tr } = useLang();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data,
  });
  const { data: featured = [] } = useQuery({
    queryKey: ["featured"], queryFn: async () => (await api.get("/products?featured=true")).data,
  });
  const { data: recipes = [] } = useQuery({
    queryKey: ["recipes-home"], queryFn: async () => (await api.get("/recipes")).data,
  });

  const values = [
    { icon: Sparkles, title: t("home.value1"), d: t("home.value1d") },
    { icon: Leaf, title: t("home.value2"), d: t("home.value2d") },
    { icon: Heart, title: t("home.value3"), d: t("home.value3d") },
    { icon: Users, title: t("home.value4"), d: t("home.value4d") },
  ];

  useEffect(() => { document.title = "Sambiva | Authentic Brazilian Food for Europe"; }, []);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className={`max-w-2xl ${mounted ? "section-fade" : "opacity-0"}`}>
            <p className="font-script text-3xl sm:text-4xl text-sambiva-yellow mb-3">{t("home.heroEyebrow")}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-[1.05] mb-5">
              {t("home.heroTitle")}
            </h1>
            <p className="text-lg text-white/90 mb-2">{t("home.heroSub")}</p>
            <p className="text-base text-white/75 mb-8">{t("home.heroLine")}</p>
            <div className="flex flex-wrap items-center gap-4">
              <button
                data-testid="hero-explore-btn"
                onClick={() => navigate("/products")}
                className="bg-sambiva-red hover:bg-sambiva-deepred text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:shadow-xl hover:-translate-y-0.5"
              >
                {t("home.exploreBtn")}
              </button>
              <button
                data-testid="hero-where-btn"
                onClick={() => navigate("/where-to-buy")}
                className="bg-white/95 hover:bg-white text-sambiva-charcoal font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
              >
                {t("home.whereBtn")}
              </button>
              <button
                data-testid="hero-trade-link"
                onClick={() => navigate("/trade")}
                className="text-white/90 hover:text-sambiva-yellow font-medium underline underline-offset-4 transition-colors"
              >
                {t("home.heroTitle") && t("home.tradeLink")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="max-w-2xl mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl text-sambiva-charcoal mb-3">{t("home.categoriesTitle")}</h2>
          <p className="text-sambiva-muted text-lg">{t("home.categoriesSub")}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {categories.map((c) => (
            <div
              key={c.id}
              data-testid={`category-card-${c.slug}`}
              onClick={() => navigate(`/products?category=${encodeURIComponent(c.name)}`)}
              className="group relative rounded-3xl overflow-hidden aspect-[4/5] cursor-pointer card-hover"
            >
              <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-sambiva-charcoal/85 via-sambiva-charcoal/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="font-serif text-xl text-white mb-1">{c.name}</h3>
                <p className="text-sm text-white/80 line-clamp-2 mb-2">{c.description}</p>
                <span className="text-xs font-semibold text-sambiva-yellow flex items-center gap-1">
                  {t("common.viewProducts")} <ArrowRight size={13} />
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <h2 className="font-serif text-3xl sm:text-4xl text-sambiva-charcoal mb-3">{t("home.featuredTitle")}</h2>
            <p className="text-sambiva-muted text-lg">{t("home.featuredSub")}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {featured.slice(0, 8).map((p) => (
              <ProductCard key={p.id} product={p} t={t} tr={tr} onView={(slug) => navigate(`/products/${slug}`)} />
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <h2 className="font-serif text-3xl sm:text-4xl text-sambiva-charcoal text-center mb-14">{t("home.valuesTitle")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((v, i) => (
            <div key={i} className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-sambiva-green/10 flex items-center justify-center mx-auto mb-4">
                <v.icon className="text-sambiva-green" size={28} />
              </div>
              <h3 className="font-serif text-xl text-sambiva-charcoal mb-2">{v.title}</h3>
              <p className="text-sm text-sambiva-muted leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="bg-sambiva-green text-sambiva-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/3] order-2 md:order-1">
            <img src="https://images.unsplash.com/photo-1578496780896-7081cc23c111?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Family sharing food" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="order-1 md:order-2">
            <p className="font-script text-3xl text-sambiva-yellow mb-3">Good food brings people together</p>
            <h2 className="font-serif text-3xl sm:text-4xl mb-5">{t("home.storyTitle")}</h2>
            <p className="text-sambiva-cream/85 text-lg leading-relaxed mb-7">{t("home.storyBody")}</p>
            <button
              data-testid="home-story-btn"
              onClick={() => navigate("/our-story")}
              className="inline-flex items-center gap-2 bg-sambiva-yellow text-sambiva-charcoal font-semibold px-6 py-3 rounded-full hover:bg-white transition-colors"
            >
              {t("home.storyBtn")} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* RECIPES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="flex items-end justify-between mb-12 gap-4">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl text-sambiva-charcoal mb-3">{t("home.recipesTitle")}</h2>
            <p className="text-sambiva-muted text-lg">{t("home.recipesSub")}</p>
          </div>
          <button data-testid="home-recipes-btn" onClick={() => navigate("/recipes")} className="hidden sm:inline-flex items-center gap-2 text-sambiva-red font-semibold hover:underline shrink-0">
            {t("home.recipesBtn")} <ArrowRight size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {recipes.slice(0, 3).map((r) => (
            <div key={r.id} data-testid={`recipe-card-${r.slug}`} onClick={() => navigate(`/recipes/${r.slug}`)} className="group bg-white rounded-3xl overflow-hidden card-hover cursor-pointer border border-sambiva-charcoal/5">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-5">
                <h3 className="font-serif text-xl text-sambiva-charcoal mb-2">{r.title}</h3>
                <div className="flex items-center gap-4 text-sm text-sambiva-muted">
                  <span className="flex items-center gap-1"><Clock size={14} /> {r.prep_time}</span>
                  <span className="px-2 py-0.5 rounded-full bg-sambiva-cream text-xs">{r.difficulty}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHERE TO BUY strip */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <MapPin className="mx-auto text-sambiva-red mb-3" size={32} />
          <h2 className="font-serif text-3xl sm:text-4xl text-sambiva-charcoal mb-3">{t("home.whereTitle")}</h2>
          <p className="text-sambiva-muted text-lg mb-7 max-w-xl mx-auto">{t("home.whereSub")}</p>
          <button data-testid="home-where-btn" onClick={() => navigate("/where-to-buy")} className="bg-sambiva-green hover:bg-sambiva-deepgreen text-white font-semibold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
            {t("home.whereBtn")}
          </button>
        </div>
      </section>

      {/* TRADE CTA */}
      <section className="relative bg-sambiva-red overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <span className="inline-block text-xs font-bold tracking-widest text-white/80 uppercase mb-4">Sambiva Trade</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-5">{t("home.tradeTitle")}</h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto mb-9">{t("home.tradeBody")}</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button data-testid="home-trade-partner-btn" onClick={() => navigate("/partner")} className="bg-white text-sambiva-red font-semibold px-7 py-3.5 rounded-full hover:bg-sambiva-cream transition-all hover:-translate-y-0.5">
              {t("nav.becomePartner")}
            </button>
            <button data-testid="home-trade-login-btn" onClick={() => navigate("/trade/login")} className="border-2 border-white text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors">
              {t("nav.tradeLogin")}
            </button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
